import { D, fiscalDate, money, normalizeNuit, NUIT_RE } from '@makila/fiscal';
import { CashError } from './cash.js';
import { nextEvent } from './outbox.js';
import { cartTotals, settle } from './sale.js';
import { checkCustomer, issueDocument, newVoucherCode, TerminalError, } from './terminal.js';
export const reservationPaid = (r) => money(r.advances.reduce((s, a) => s.plus(a.amount), new D(0)));
export const reservationRest = (r) => money(D.max(new D(r.total).minus(reservationPaid(r)), 0));
export const reservationOverdue = (r, now = new Date()) => r.status === 'open' && r.dueDate < fiscalDate(now);
/**
 * Linhas da FR de adiantamento: o valor repartido pelas taxas de IVA das peças
 * (na proporção do total de cada taxa), uma linha por taxa, ao cêntimo.
 */
export function advanceLines(r, amount) {
    const totals = cartTotals(r.items);
    const groups = new Map();
    r.items.forEach((it, i) => {
        const k = `${new D(it.taxRate).toString()}|${it.exemptionCode ?? ''}`;
        const g = groups.get(k) ?? { taxRate: new D(it.taxRate).toString(), exemptionCode: it.exemptionCode ?? null, gross: new D(0) };
        g.gross = g.gross.plus(totals.lines[i].totalAmount);
        groups.set(k, g);
    });
    const all = [...groups.values()].filter((g) => g.gross.greaterThan(0)).sort((a, b) => new D(b.taxRate).comparedTo(a.taxRate));
    const total = new D(totals.grossTotal);
    let left = new D(amount);
    return all.map((g, i) => {
        const share = i === all.length - 1 ? left : new D(amount).times(g.gross).div(total).toDecimalPlaces(2);
        left = left.minus(share);
        return {
            productId: r.id, description: `Adiantamento — reserva ${r.code}${all.length > 1 ? ` (IVA ${g.taxRate.replace('.', ',')}%)` : ''}`,
            quantity: '1', unitPrice: money(share), taxRate: g.taxRate, priceIncludesTax: true, ...(g.exemptionCode ? { exemptionCode: g.exemptionCode } : {}),
        };
    }).filter((l) => new D(l.unitPrice).greaterThan(0));
}
function checkAmount(v, what) {
    const a = new D(v);
    if (!a.greaterThan(0) || a.decimalPlaces() > 2)
        throw new TerminalError('reservation', `${what} inválido`);
    return money(a);
}
async function openSessionOf(tx, id) {
    const s = await tx.openSession();
    if (!s || s.id !== id)
        throw new CashError('no_session', 'Abra a caixa antes de receber');
    return s;
}
/** FR de adiantamento de uma reserva, dentro da transacção de quem chama. */
async function issueAdvance(tx, who, signer, r, p, newId) {
    const lines = advanceLines(r, p.amount);
    const totals = cartTotals(lines);
    const pay = settle(totals.grossTotal, p.tenders);
    const paidBefore = reservationPaid(r);
    return issueDocument(tx, who, signer, 'FR', p.now, newId, () => ({
        lines: lines.map((l, i) => ({ ...l, netAmount: totals.lines[i].netAmount, taxAmount: totals.lines[i].taxAmount, totalAmount: totals.lines[i].totalAmount })),
        netTotal: totals.netTotal, taxTotal: totals.taxTotal, taxSummary: totals.taxSummary,
        payments: pay.payments, tendered: pay.tendered, change: pay.change,
        customer: { ...(r.customer.id ? { id: r.customer.id } : {}), name: r.customer.name, nuit: r.customer.nuit },
        operatorId: p.operatorId, cashSessionId: p.sessionId,
        advance: { reservationId: r.id, code: r.code, total: r.total, paidBefore, dueDate: r.dueDate, items: r.items.map((i) => ({ description: i.description, quantity: i.quantity })) },
    }), totals.grossTotal);
}
/** O adiantamento acabado de emitir, na forma da reserva (para a caixa a actualizar já, sem esperar pela nuvem). */
export const advanceFromDocument = (d) => ({
    seriesId: d.seriesId, number: d.number, fullNumber: d.fullNumber, issueDate: d.issueDate, docType: 'FR', training: d.training,
    amount: money(d.lines.reduce((s, l) => s.plus(l.totalAmount), new D(0))), customer: d.customer, lines: d.lines,
});
/**
 * Nova reserva: as peças, o cliente (com NUIT), o prazo para levantar e o
 * sinal, pago já (FR de adiantamento). A reserva e a FR entram juntas.
 */
export async function createReservation(store, who, signer, p, newId = () => globalThis.crypto.randomUUID()) {
    if (!p.licence.sell)
        throw new TerminalError('cannot_sell', p.licence.reason ?? 'Licença sem venda');
    if (!p.items.length)
        throw new TerminalError('empty_cart', 'Sem peças para reservar');
    if (p.items.some((i) => !i.promotionId && !new D(i.discountPct ?? 0).isZero()))
        throw new TerminalError('reservation', 'Os descontos manuais não entram numa reserva: aplicam-se no levantamento');
    const c = checkCustomer({ ...(p.customer.id ? { id: p.customer.id } : {}), name: p.customer.name, nuit: p.customer.nuit });
    const nuit = normalizeNuit(c.nuit);
    if (!nuit || !NUIT_RE.test(nuit))
        throw new TerminalError('nuit_required', 'A reserva com sinal precisa do NUIT do cliente (a factura de adiantamento e a nota de crédito do levantamento levam-no)');
    const now = p.now ?? new Date();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(p.dueDate) || p.dueDate < fiscalDate(now))
        throw new TerminalError('reservation', 'O prazo para levantar tem de ser hoje ou depois');
    const total = cartTotals(p.items).grossTotal;
    const deposit = checkAmount(p.deposit, 'Sinal');
    if (new D(deposit).greaterThan(total))
        throw new TerminalError('reservation', `O sinal passa o total da reserva (${money(total)} MT)`);
    const r = {
        id: newId(), code: newVoucherCode(), storeId: null, status: 'open', dueDate: p.dueDate, total: money(total), advances: [],
        customer: { ...(c.id ? { id: c.id } : {}), name: c.name, nuit, phone: p.customer.phone?.trim() || null },
        items: p.items.map((i) => ({ ...i, quantity: new D(i.quantity).toString(), unitPrice: new D(i.unitPrice).toString(), taxRate: new D(i.taxRate).toString() })),
    };
    return store.transaction(async (tx) => {
        const s = await openSessionOf(tx, p.cashSessionId);
        await tx.appendOutbox(nextEvent({ terminalId: who.terminalId, lastSeq: await tx.lastSeq(), type: 'reservation.created', now,
            payload: { id: r.id, code: r.code, customer: r.customer, items: r.items, total: r.total, dueDate: r.dueDate, operatorId: p.operatorId, at: now.toISOString() } }));
        const document = await issueAdvance(tx, who, signer, r, { amount: deposit, tenders: p.tenders, operatorId: p.operatorId, sessionId: s.id, now }, newId);
        return { reservation: { ...r, advances: [advanceFromDocument(document)] }, document };
    });
}
/** Reforço do sinal (pagamento parcial): outra FR de adiantamento, até ao total. */
export async function payReservation(store, who, signer, p, newId = () => globalThis.crypto.randomUUID()) {
    if (!p.licence.sell)
        throw new TerminalError('cannot_sell', p.licence.reason ?? 'Licença sem venda');
    const r = p.reservation;
    if (r.status !== 'open')
        throw new TerminalError('reservation', `A reserva ${r.code} já está fechada`);
    const amount = checkAmount(p.amount, 'Valor');
    if (new D(amount).greaterThan(reservationRest(r)))
        throw new TerminalError('reservation', `Só faltam ${money(reservationRest(r))} MT`);
    const now = p.now ?? new Date();
    return store.transaction(async (tx) => {
        const s = await openSessionOf(tx, p.cashSessionId);
        return issueAdvance(tx, who, signer, r, { amount, tenders: p.tenders, operatorId: p.operatorId, sessionId: s.id, now }, newId);
    });
}
/** NC que abate (ou devolve) um adiantamento inteiro: o que ainda não foi creditado de cada linha. */
async function creditAdvance(tx, who, signer, r, a, p, newId) {
    const local = await tx.creditedQuantities(a.seriesId, a.number);
    const lines = a.lines.flatMap((src, i) => {
        const done = D.max(new D(local[i] ?? 0), new D(a.credited?.[i] ?? 0));
        const left = new D(src.quantity).minus(done);
        if (!left.greaterThan(0))
            return [];
        const { netAmount: _n, taxAmount: _t, totalAmount: _tt, ...base } = src;
        return [{ ...base, quantity: left.toString(), originLine: i }];
    });
    if (!lines.length)
        return null;
    const totals = cartTotals(lines);
    const pay = settle(totals.grossTotal, [{ ...p.refund, amount: totals.grossTotal }]);
    return issueDocument(tx, who, signer, 'NC', p.now, newId, () => ({
        lines: lines.map((l, i) => ({ ...l, netAmount: totals.lines[i].netAmount, taxAmount: totals.lines[i].taxAmount, totalAmount: totals.lines[i].totalAmount })),
        netTotal: totals.netTotal, taxTotal: totals.taxTotal, taxSummary: totals.taxSummary,
        payments: pay.payments, tendered: pay.tendered, change: '0.00',
        customer: a.customer, operatorId: p.operatorId, cashSessionId: p.sessionId,
        origin: { seriesId: a.seriesId, number: a.number, fullNumber: a.fullNumber, issueDate: a.issueDate, docType: 'FR' },
        reason: p.reason, approvedBy: p.approvedBy,
        reservation: { id: r.id, code: r.code, ...(p.deducted ? { deducted: p.deducted } : {}) },
    }), totals.grossTotal);
}
/**
 * Levantamento: FR das peças pelo valor total, paga com os adiantamentos e o
 * que falta; cada adiantamento abatido por NC sobre a sua FR. Numa transacção.
 */
export async function collectReservation(store, who, signer, p, newId = () => globalThis.crypto.randomUUID()) {
    if (!p.licence.sell)
        throw new TerminalError('cannot_sell', p.licence.reason ?? 'Licença sem venda');
    const r = p.reservation;
    if (r.status !== 'open')
        throw new TerminalError('reservation', `A reserva ${r.code} já está fechada`);
    if (r.advances.some((a) => a.training !== who.training))
        throw new TerminalError('training_mismatch', 'Documento de formação e real não se misturam');
    const totals = cartTotals(r.items);
    const paid = new D(reservationPaid(r));
    if (paid.greaterThan(totals.grossTotal))
        throw new TerminalError('reservation', 'Os adiantamentos passam o total: anule a reserva e devolva a diferença');
    const credits = r.advances.map((a) => ({ method: 'OU', amount: a.amount, reference: `Adiantamento ${a.fullNumber}` }));
    const pay = settle(totals.grossTotal, [...credits, ...p.tenders]);
    const now = p.now ?? new Date();
    return store.transaction(async (tx) => {
        const s = await openSessionOf(tx, p.cashSessionId);
        const document = await issueDocument(tx, who, signer, 'FR', now, newId, () => ({
            lines: r.items.map((l, i) => ({ ...l, netAmount: totals.lines[i].netAmount, taxAmount: totals.lines[i].taxAmount, totalAmount: totals.lines[i].totalAmount })),
            netTotal: totals.netTotal, taxTotal: totals.taxTotal, taxSummary: totals.taxSummary,
            payments: pay.payments, tendered: pay.tendered, change: pay.change,
            customer: { ...(r.customer.id ? { id: r.customer.id } : {}), name: r.customer.name, nuit: r.customer.nuit },
            operatorId: p.operatorId, cashSessionId: s.id,
            reservation: { id: r.id, code: r.code, deducted: money(paid) },
        }), totals.grossTotal);
        const creditNotes = [];
        for (const a of r.advances) {
            const nc = await creditAdvance(tx, who, signer, r, a, {
                refund: { method: 'OU', amount: a.amount, reference: `Abatido em ${document.fullNumber}` },
                reason: `Adiantamento abatido na factura-recibo ${document.fullNumber} (levantamento da reserva ${r.code})`,
                approvedBy: p.operatorId, operatorId: p.operatorId, sessionId: s.id, now, deducted: a.amount,
            }, newId);
            if (nc)
                creditNotes.push(nc);
        }
        return { document, creditNotes };
    });
}
/**
 * Anular a reserva (com um gerente). Com devolução: cada adiantamento sai por
 * NC e o dinheiro devolve-se pelo meio escolhido. Sem devolução (o cliente
 * desistiu e o sinal fica, conforme combinado), as FR ficam como estão.
 */
export async function cancelReservation(store, who, signer, p, newId = () => globalThis.crypto.randomUUID()) {
    const r = p.reservation;
    if (r.status !== 'open')
        throw new TerminalError('reservation', `A reserva ${r.code} já está fechada`);
    if (!p.reason.trim())
        throw new TerminalError('reason_required', 'Indique o motivo');
    if (!p.approvedBy)
        throw new TerminalError('approval_required', 'Anular uma reserva precisa de um gerente');
    if (p.refund && !p.licence.sell)
        throw new TerminalError('cannot_sell', p.licence.reason ?? 'Licença sem venda');
    const now = p.now ?? new Date();
    return store.transaction(async (tx) => {
        const s = await openSessionOf(tx, p.cashSessionId);
        const creditNotes = [];
        if (p.refund) {
            for (const a of r.advances) {
                const nc = await creditAdvance(tx, who, signer, r, a, {
                    refund: { ...p.refund, amount: a.amount }, reason: `Reserva ${r.code} anulada: ${p.reason.trim()}`.slice(0, 300),
                    approvedBy: p.approvedBy, operatorId: p.operatorId, sessionId: s.id, now,
                }, newId);
                if (nc)
                    creditNotes.push(nc);
            }
        }
        await tx.appendOutbox(nextEvent({ terminalId: who.terminalId, lastSeq: await tx.lastSeq(), type: 'reservation.cancelled', now,
            payload: { id: r.id, refunded: !!p.refund, reason: p.reason.trim().slice(0, 300), approvedBy: p.approvedBy, operatorId: p.operatorId, at: now.toISOString() } }));
        return { creditNotes };
    });
}
//# sourceMappingURL=deposits.js.map